// src/pages/about/About.styled.tsx
import styled from "styled-components";

export const AboutContainer = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: 2rem 1.5rem;
`;

export const PageTitle = styled.h1`
  font-size: 2.5rem;
  font-weight: 700;
  margin-bottom: 1.5rem;
  color: ${({ theme }) => theme.colors.text};
  text-align: center;
  position: relative;

  &:after {
    content: "";
    position: absolute;
    bottom: -10px;
    left: 50%;
    transform: translateX(-50%);
    width: 80px;
    height: 4px;
    background: linear-gradient(90deg, #6c63ff, #5046e4);
    border-radius: 2px;
  }
`;

export const SectionTitle = styled.h2`
  font-size: 1.75rem;
  font-weight: 600;
  margin: 2.5rem 0 1.5rem;
  color: ${({ theme }) => theme.colors.text};
  position: relative;
  padding-left: 1rem;
  border-left: 4px solid #6c63ff;
`;

export const AboutMeSection = styled.section`
  display: flex;
  flex-direction: column;
  margin-bottom: 3rem;
  gap: 2rem;

  @media (min-width: 768px) {
    flex-direction: row;
    align-items: center;
  }
`;

export const AboutContent = styled.div`
  flex: 2;
`;

export const AboutImageWrapper = styled.div`
  flex: 1;
  display: flex;
  justify-content: center;

  img {
    max-width: 100%;
    height: auto;
    border-radius: 12px;
    box-shadow: 0 8px 20px rgba(0, 0, 0, 0.1);
  }
`;

export const Paragraph = styled.p`
  font-size: 1.1rem;
  line-height: 1.7;
  color: ${({ theme }) => theme.colors.textSecondary};
  margin-bottom: 1.5rem;
`;

export const Timeline = styled.div`
  position: relative;
  max-width: 1200px;
  margin: 2rem auto;

  &:before {
    content: "";
    position: absolute;
    width: 4px;
    background: linear-gradient(180deg, #6c63ff, #b4b0ff);
    top: 0;
    bottom: 0;
    left: 0;
    margin-left: 20px;

    @media (min-width: 768px) {
      left: 50%;
      transform: translateX(-50%);
      margin-left: 0;
    }
  }
`;

export const TimelineItem = styled.div`
  padding: 10px 40px;
  position: relative;
  background-color: inherit;
  width: 100%;
  margin-bottom: 2rem;

  @media (min-width: 768px) {
    width: 50%;

    &:nth-child(odd) {
      left: 0;
    }

    &:nth-child(even) {
      left: 50%;
    }
  }
`;

export const TimelineContent = styled.div`
  padding: 1.5rem;
  background-color: white;
  position: relative;
  border-radius: 12px;
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.05);
  transition: transform 0.3s ease, box-shadow 0.3s ease;

  &:hover {
    transform: translateY(-5px);
    box-shadow: 0 8px 24px rgba(0, 0, 0, 0.1);
  }
`;

export const TimelineDot = styled.div`
  position: absolute;
  width: 25px;
  height: 25px;
  border-radius: 50%;
  background-color: #6c63ff;
  top: 20px;
  z-index: 1;
  left: 18px;

  @media (min-width: 768px) {
    left: auto;
    right: -12px;

    ${TimelineItem}:nth-child(even) & {
      left: -12px;
      right: auto;
    }
  }
`;

export const TimelineTitle = styled.h3`
  font-size: 1.3rem;
  font-weight: 700;
  color: ${({ theme }) => theme.colors.darkBlue};
  margin-bottom: 0.5rem;
`;

export const TimelineSubtitle = styled.h4`
  font-size: 1.1rem;
  font-weight: 500;
  color: #6c63ff;
  margin-bottom: 0.5rem;
`;

export const TimelineDate = styled.div`
  font-size: 0.9rem;
  color: ${({ theme }) => theme.colors.textTertiary};
  font-weight: 500;
  display: inline-block;
  padding: 0.25rem 0.75rem;
  background-color: ${({ theme }) => theme.colors.backgroundSecondary};
  border-radius: 20px;
  margin: 0.5rem 0;
`;

export const TimelineDescription = styled.p`
  font-size: 1rem;
  line-height: 1.6;
  color: ${({ theme }) => theme.colors.textSecondary};
  margin-top: 1rem;
`;

export const TechTags = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem;
  margin-top: 1rem;
`;

export const TechTag = styled.span`
  padding: 0.25rem 0.75rem;
  background-color: #f0f0ff;
  color: #6c63ff;
  border-radius: 20px;
  font-size: 0.85rem;
  font-weight: 500;
`;

export const CardGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(300px, 1fr));
  gap: 1.5rem;
  margin-top: 1.5rem;
`;

export const Card = styled.div`
  background-color: white;
  border-radius: 12px;
  padding: 1.5rem;
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.05);
  transition: transform 0.3s ease, box-shadow 0.3s ease;

  &:hover {
    transform: translateY(-5px);
    box-shadow: 0 8px 24px rgba(0, 0, 0, 0.1);
  }
`;

export const CardTitle = styled.h3`
  font-size: 1.2rem;
  font-weight: 600;
  color: #333;
  margin-bottom: 0.5rem;
`;

export const CardDescription = styled.p`
  font-size: 0.95rem;
  line-height: 1.6;
  color: #555;
`;

export const PublicationItem = styled.div`
  margin-bottom: 1.5rem;
  padding-bottom: 1.5rem;
  border-bottom: 1px solid #eaeaea;

  &:last-child {
    border-bottom: none;
  }
`;

export const PublicationTitle = styled.h3`
  font-size: 1.1rem;
  font-weight: 600;
  color: #333;
  margin-bottom: 0.5rem;

  a {
    color: inherit;
    text-decoration: none;
    transition: color 0.2s;

    &:hover {
      color: #6c63ff;
    }
  }
`;

export const PublicationAuthors = styled.p`
  font-size: 0.95rem;
  color: #555;
  margin-bottom: 0.25rem;
  font-style: italic;
`;

export const PublicationJournal = styled.p`
  font-size: 0.9rem;
  color: #777;
  margin-bottom: 0.25rem;
`;

export const PublicationYear = styled.span`
  font-weight: 500;
`;

export const CertificationList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1rem;
  margin-top: 1.5rem;
`;

export const CertificationItem = styled.div`
  display: flex;
  align-items: center;
  padding: 1rem;
  background-color: #f8f8f8;
  border-radius: 8px;
  transition: background-color 0.2s;

  &:hover {
    background-color: #f0f0ff;
  }
`;

export const CertificationInfo = styled.div`
  flex: 1;
`;

export const CertificationName = styled.h4`
  font-size: 1.1rem;
  font-weight: 500;
  color: #333;
  margin-bottom: 0.25rem;
`;

export const CertificationIssuer = styled.p`
  font-size: 0.9rem;
  color: #555;
`;

export const CertificationDate = styled.span`
  font-size: 0.9rem;
  color: #777;
  padding: 0.25rem 0.75rem;
  background-color: #e8e8e8;
  border-radius: 20px;
  margin-left: 1rem;
`;

export const CertificationLink = styled.a`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 36px;
  height: 36px;
  border-radius: 50%;
  background-color: #6c63ff;
  color: white;
  margin-left: 1rem;
  transition: background-color 0.2s;

  &:hover {
    background-color: #5046e4;
  }

  svg {
    width: 18px;
    height: 18px;
  }
`;

export const ButtonWrapper = styled.div`
  display: flex;
  justify-content: center;
  margin: 3rem 0;
`;
